import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Navbar } from "./components/site/Navbar";
import { Hero } from "./components/site/Hero";
import { Specialties } from "./components/site/Specialties";
import { Treatments } from "./components/site/Treatments";
import { About } from "./components/site/About";
import { Telehealth } from "./components/site/Telehealth";
import { Testimonials } from "./components/site/Testimonials";
import { FAQ } from "./components/site/FAQ";
import { Contact } from "./components/site/Contact";
import { Footer } from "./components/site/Footer";
import { AppointmentDialog } from "./components/site/AppointmentDialog";
import { CookieBanner } from "./components/site/CookieBanner";
import JoinSession from "./pages/JoinSession";
import PrivacyNotice from "./pages/PrivacyNotice";
import WhatToExpect from "./pages/WhatToExpect";
import Fees from "./pages/Fees";
import ForPhysicians from "./pages/ForPhysicians";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import LocationPage from "./pages/LocationPage";
import { AdminAuthProvider } from "./admin/AuthContext";
import AdminLogin from "./admin/AdminLogin";
import AdminDashboard from "./admin/AdminDashboard";

import { usePageMeta } from "./hooks/usePageMeta";
import { useJsonLd } from "./hooks/useJsonLd";

const HOME_FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How does telehealth speech therapy work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Before each session Kathleen sends you a private Zoom for Healthcare link. You click 'Join your session' on the website, confirm a short HIPAA / GDPR consent, and the secure video room opens in a new tab. No app sign-up, no patient portal — just a browser, headphones, and a quiet room.",
      },
    },
    {
      "@type": "Question",
      name: "Are you accepting new clients?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Use the 'Book a Consult' button to request an appointment. Kathleen replies within 1–2 business days with available times, a brief intake form, and the first session details.",
      },
    },
    {
      "@type": "Question",
      name: "Do you take insurance?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sessions are private pay. You will receive itemised receipts and a superbill on request — these can be submitted for out-of-network reimbursement in the USA, and for tax relief in Ireland (Med 2 form, where applicable).",
      },
    },
    {
      "@type": "Question",
      name: "How long is a session and how many will I need?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Most adult sessions run 45–60 minutes. SPEAK OUT!® for Parkinson's is a defined 12-session course; cognitive-communication therapy runs 8–12 weeks; swallow management is reassessed every 4–6 weeks.",
      },
    },
    {
      "@type": "Question",
      name: "What is a swallow evaluation and do I need one?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "If eating or drinking has become difficult, painful, or you've experienced coughing, throat clearing or unexplained weight loss, a clinical swallow evaluation can identify the cause and guide safe, enjoyable mealtimes. Kathleen refers for an instrumental study (FEES or VFSS) when indicated.",
      },
    },
    {
      "@type": "Question",
      name: "How is my information protected?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sessions run on Zoom for Healthcare under a Business Associate Agreement (BAA). Inquiry data is stored under HIPAA (USA) and GDPR (EU/Ireland) safeguards. We capture only what's needed, hash IPs in the audit trail, and never sell or share your data.",
      },
    },
  ],
};

const HOME_WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://neurocompartners.com/#website",
  url: "https://neurocompartners.com",
  name: "Neurological Communication Partners",
  publisher: { "@id": "https://neurocompartners.com/#business" },
  inLanguage: ["en-US", "en-IE"],
};

const Home = () => {
  usePageMeta({
    title: "Adult SLP · Speech Therapy for Parkinson's, Stroke, Swallow",
    description:
      "Kathleen McGrath, M.S. CCC-SLP — adult speech-language pathologist (SLP). Parkinson's SPEAK OUT!®, post-stroke aphasia, cognitive-communication therapy and adult swallow evaluation. Licensed in New Hampshire, Vermont & Ireland. Secure HIPAA-compliant telehealth.",
    path: "/",
  });
  useJsonLd(HOME_WEBSITE_JSONLD, "home-website-jsonld");
  useJsonLd(HOME_FAQ_JSONLD, "home-faq-jsonld");
  const [open, setOpen] = useState(false);
  const openDialog = () => setOpen(true);
  return (
    <main data-testid="home-page" className="bg-[#FDFCF8] text-[#2D332F]">
      <Navbar onBookClick={openDialog} />
      <Hero onBookClick={openDialog} />
      <Specialties />
      <Treatments />
      <About />
      <Testimonials />
      <Telehealth onBookClick={openDialog} />
      <FAQ />
      <Contact onBookClick={openDialog} />
      <Footer />
      <AppointmentDialog open={open} onOpenChange={setOpen} />
      <CookieBanner />
    </main>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AdminAuthProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/join" element={<JoinSession />} />
            <Route path="/privacy" element={<PrivacyNotice />} />
            <Route path="/what-to-expect" element={<WhatToExpect />} />
            <Route path="/fees" element={<Fees />} />
            <Route path="/for-physicians" element={<ForPhysicians />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/new-hampshire" element={<LocationPage />} />
            <Route path="/vermont" element={<LocationPage />} />
            <Route path="/ireland" element={<LocationPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
        </AdminAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
